import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchVoiceListeners, Listener } from '../../api/listenersApi';
import { isAbortError, toApiError } from '../../api/http';
import { useApp } from '../../context/AppContext';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { familyVoiceStatus } from '../../copy/terminology';
import { ArrowLeftIcon, MicIcon, PencilIcon, TrashIcon } from '../../components/icons';
import { FormField, Panel, PillButton, StateMessage } from '../../components/ui';
import './Voice.css';

function isUsableVoice(voice: { status: string; consent_active?: boolean | null }) {
  return voice.status === 'ready' && voice.consent_active === true;
}

const VoiceDetails: React.FC = () => {
  const { voiceId = '' } = useParams();
  const navigate = useNavigate();
  const { state, fetchVoices, updateVoiceNickname, deleteVoice } = useApp();
  const bedtime = useBedtimeContext();
  const voice = state.voices.find((candidate) => candidate.voice_id === voiceId);
  const [listeners, setListeners] = useState<Listener[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ReturnType<typeof toApiError> | null>(null);

  useEffect(() => {
    if (voice || state.voices.length) return undefined;
    let active = true;
    fetchVoices(true).catch((requestError) => {
      if (active) setError(toApiError(requestError));
    });
    return () => { active = false; };
  }, [fetchVoices, state.voices.length, voice]);

  useEffect(() => {
    if (!voice) {
      if (state.voices.length) setLoading(false);
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchVoiceListeners(voice.voice_id, controller.signal)
      .then((rows) => { if (active) setListeners(rows); })
      .catch((requestError) => {
        if (active && !isAbortError(requestError)) setError(toApiError(requestError));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [retryNonce, state.voices.length, voice]);

  useEffect(() => {
    if (voice) setName(voice.voice_name);
  }, [voice]);

  const saveName = async () => {
    if (!voice || !name.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await updateVoiceNickname(voice.voice_id, name.trim());
      if (!updated) {
        setActionError(toApiError(null));
        return;
      }
      setEditing(false);
    } catch (requestError) {
      setActionError(toApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const removeVoice = async () => {
    if (!voice || !window.confirm(`Delete ${voice.voice_name}? Its saved voice and listener personalization will no longer be available for bedtime.`)) return;
    setBusy(true);
    setActionError(null);
    try {
      const deleted = await deleteVoice(voice.voice_id);
      if (deleted) {
        await bedtime.refresh();
        navigate('/voice');
      }
    } catch (requestError) {
      setActionError(toApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !voice) return <div className="voice"><StateMessage kind="loading" title="Loading Family voice…" shape="form" headingLevel={1} /></div>;
  if (error) return <div className="voice"><StateMessage kind="error" error={error} title="This Family voice could not be opened" headingLevel={1} retry={{ onClick: () => setRetryNonce((nonce) => nonce + 1) }} /></div>;
  if (!voice) return <div className="voice"><StateMessage kind="blocked" title="That Family voice is unavailable" message="It may have been removed or you may no longer have access to it." headingLevel={1} retry={{ label: 'Family voices', to: '/voice' }} /></div>;

  const complete = listeners.filter((listener) => listener.state === 'active' && listener.bookend_complete).length;
  const activeListeners = listeners.filter((listener) => listener.state === 'active');
  const usable = isUsableVoice(voice);

  return (
    <div className="voice voice-details">
      <div className="voice__back">
        <PillButton variant="secondary" size="compact" to="/voice" icon={<ArrowLeftIcon size={20} />}>Family voices</PillButton>
      </div>
      <header className="voice-detail__head">
        <span className="voice-detail__portrait" aria-hidden="true">{voice.profile_picture_url ? <img src={voice.profile_picture_url} alt="" /> : <MicIcon size={24} />}</span>
        <div>
          <h1>{voice.voice_name}</h1>
          <p className="voice-detail__status">{familyVoiceStatus(voice.status, voice.consent_active, voice.sample_count)}</p>
        </div>
      </header>
      {actionError && <StateMessage kind="error" urgent error={actionError} title="That change didn’t save" />}

      <Panel purpose="settings" labelledBy="voice-readiness-title" className="voice-panel">
        <div className="voice-panel__head">
          <h2 id="voice-readiness-title">Personal messages</h2>
          <span className="voice-panel__count">{complete}/{activeListeners.length}</span>
        </div>
        {loading && <StateMessage kind="loading" title="Checking personal messages…" shape="text" />}
        {!loading && !activeListeners.length && (
          <div className="voice-panel__empty">
            <p><strong>No active listeners yet</strong></p>
            <p>Add a listener in Account &amp; family before personalizing this voice.</p>
            <PillButton variant="secondary" size="compact" to="/account">Account &amp; family</PillButton>
          </div>
        )}
        {!loading && activeListeners.length > 0 && (
          <>
            <p className="voice-panel__note">{complete} of {activeListeners.length} active listener{activeListeners.length === 1 ? '' : 's'} have a personal hello, name, and goodnight.</p>
            <ul className="voice-listeners">
              {activeListeners.map((listener) => (
                <li className="voice-listener" key={listener.listener_id}>
                  <div className="voice-listener__text">
                    <strong>{listener.display_name}</strong>
                    <span>{listener.bookend_complete ? 'Ready for bedtime' : 'Needs hello and goodnight'}</span>
                  </div>
                  {usable
                    ? <PillButton variant="secondary" size="compact" to={`/voice/${voice.voice_id}/personalize/${listener.listener_id}`}>{listener.bookend_complete ? 'Update moments' : 'Personalize'}</PillButton>
                    : <span className="voice-listener__muted">Available when ready</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      <Panel purpose="settings" labelledBy="voice-management-title" className="voice-panel">
        <h2 id="voice-management-title">Manage this voice</h2>
        {editing ? (
          <form className="voice-edit-form" onSubmit={(event) => { event.preventDefault(); void saveName(); }}>
            <FormField id="voice-name" label="Family voice name">
              <input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} autoFocus />
            </FormField>
            <div className="voice-panel__actions">
              <PillButton variant="primary" type="submit" disabled={busy || !name.trim()} busy={busy} busyLabel="Saving…">Save name</PillButton>
              <PillButton variant="secondary" onClick={() => { setEditing(false); setName(voice.voice_name); }}>Cancel</PillButton>
            </div>
          </form>
        ) : (
          <div className="voice-panel__actions">
            <PillButton variant="secondary" size="compact" icon={<PencilIcon size={18} />} onClick={() => setEditing(true)}>Rename</PillButton>
            <PillButton variant="secondary" size="compact" to={`/voice/new?voiceId=${encodeURIComponent(voice.voice_id)}`}>Record more samples</PillButton>
            <PillButton variant="destructive" size="compact" icon={<TrashIcon size={18} />} disabled={busy} onClick={() => void removeVoice()}>Delete voice</PillButton>
          </div>
        )}
        {voice.status === 'ready' && voice.consent_active !== true && <p className="voice-panel__notice">Consent for this voice is inactive. It cannot be used for bedtime until consent is active again.</p>}
        {usable && (
          <div className="voice-panel__use">
            <PillButton variant={editing ? 'secondary' : 'primary'} onClick={() => { bedtime.openPairDialog({ voiceId: voice.voice_id }); navigate('/discover'); }}>Use for bedtime</PillButton>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default VoiceDetails;
